import { Divider } from "@affinity/components/Divider";
import { Section } from "@affinity/components/Section";
import { isEmpty, phoneValidator } from "@affinity/components/validators";
import { rem } from "@affinity/constants/StyleHelpers";
import PlaceOrderSetDeliveryInstructions from "@affinity/Handlers/PlaceOrderSetDeliveryInstructions";
import { CustomState } from "@affinity/Store/Reducers";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    // Map required state elements to component properties
    billToId: state.context.session.billToId!, // Should exist if user is logged in
    savedPhone: state.affinity_place_order.phone,
});

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    PlaceOrderSetDeliveryInstructions,
};

type Step5ReviewOrderProps = ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> & { contentId: string };

export const Step5ReviewOrder: React.FC<Step5ReviewOrderProps> = ({
    savedPhone,
    contentId,
    billToId,
    PlaceOrderSetDeliveryInstructions,
}) => {
    const [state, setState] = React.useState({
        phone: "",
        useDifferentPhone: "false",
        deliveryInstructions: "",
    });
    const [error, setError] = React.useState({
        phone: "",
        useDifferentPhone: "",
        deliveryInstructions: "",
    });

    const deliveryInstructionsValidator = (instructions: string) => {
        const text = `Call ${state.phone || savedPhone} ${instructions}`;
        return text.length > 44 ? "Instructions are too long" : "";
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { target } = event;
        const propertyName = target.name;
        setState({
            ...state,
            [propertyName]: target.value,
        });
    };

    const validateInputChange =
        (validator: (val: string) => string | Promise<string>) =>
        async (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            handleInputChange(event);
            const {
                target: { value, name: propertyName },
            } = event;
            const error = await validator(value);
            setError(e => ({
                ...e,
                [propertyName]: error,
            }));
        };

    const submit = () =>
        PlaceOrderSetDeliveryInstructions({
            // Send undefined if phone not changed. We only want to know number if different
            phone: savedPhone && state.useDifferentPhone === "false" ? undefined : state.phone,
            deliveryInstructions: state.deliveryInstructions,
            billToId,
        });

    return (
        <Section title="5. Delivery instructions" shouldFocus={true}>
            <Flex>
                <Typography variant="h3">{translate("Confirm contact number")}</Typography>
                {savedPhone && (
                    <RadioGroup
                        name="useDifferentPhone"
                        value={state.useDifferentPhone}
                        onChangeHandler={handleInputChange}
                    >
                        <Radio value="false">{savedPhone}</Radio>
                        <Radio value="true">{translate("Use a different contact number")}</Radio>
                    </RadioGroup>
                )}
                {(state.useDifferentPhone === "true" || !savedPhone) && (
                    <TextField
                        type="tel"
                        name="phone"
                        onChange={validateInputChange(phoneValidator)}
                        error={error.phone}
                    />
                )}
            </Flex>
            <Zone contentId={contentId} zoneName="PlaceOrderStep5" requireRows={false} />
            <TextArea
                label="Special instructions"
                id="instructions"
                name="deliveryInstructions"
                value={state.deliveryInstructions}
                onChange={validateInputChange(deliveryInstructionsValidator)}
                error={error.deliveryInstructions}
            />
            <Divider />
            <Button
                type="submit"
                variant="primary"
                sizeVariant="large"
                icon={{ position: "right", src: "ChevronRight" }}
                css={css`
                    align-self: end;
                `}
                onClick={submit}
                disabled={
                    ((state.useDifferentPhone === "true" || !savedPhone) && (!!error.phone || isEmpty(state.phone))) ||
                    !!error.deliveryInstructions
                }
            >
                {translate("Continue")}
            </Button>
        </Section>
    );
};

const Flex = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
`;

export default connect(mapStateToProps, mapDispatchToProps)(Step5ReviewOrder);
